import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';

import { useSnackbar } from '../../../contexts/SnackbarContext';
import { useDeleteTipShareShiftMutation } from '../../requests/mutations';
import { TipShareShift } from '../../types';
import { dialogActionsSx } from '../styles';

interface DeleteShiftDialogProps {
  open: boolean;
  onClose: () => void;
  shift: TipShareShift | undefined;
}

function DeleteShiftDialog({ open, onClose, shift }: Readonly<DeleteShiftDialogProps>) {
  const { t } = useTranslation();
  const { setSnackbarState } = useSnackbar();

  const deleteMutation = useDeleteTipShareShiftMutation();
  const isDeleting = deleteMutation.isLoading;

  const handleConfirmDelete = async () => {
    if (!shift) return;

    try {
      await deleteMutation.mutateAsync(shift.id);
      setSnackbarState({
        open: true,
        message: t('labor.tipShareShifts.messages.success', { eventType: 'deleted' }),
        color: 'success',
      });
      onClose();
    } catch {
      setSnackbarState({
        open: true,
        message: t('labor.tipShareShifts.messages.error'),
        color: 'error',
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth data-testid='delete-shift-dialog'>
      <DialogTitle data-testid='delete-dialog-title'>{t('labor.tipShareShifts.dialog.deleteTitle')}</DialogTitle>
      <DialogContent>
        <Typography>{t('labor.tipShareShifts.dialog.deleteMessage', { shiftName: shift?.name ?? '' })}</Typography>
      </DialogContent>
      <DialogActions sx={dialogActionsSx}>
        <Button onClick={onClose} data-testid='cancel-delete-button'>
          {t('buttonText.cancel')}
        </Button>
        <Button
          onClick={handleConfirmDelete}
          variant='contained'
          color='error'
          disabled={isDeleting}
          data-testid='confirm-delete-button'
        >
          {isDeleting ? <CircularProgress size={24} /> : t('buttonText.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DeleteShiftDialog;
